import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import Icon from '../../components/AppIcon';
import Button from '../../components/ui/Button';
import Header from '../../components/ui/Header';
import SessionTable from './components/SessionTable';
import SessionDetailPanel from './components/SessionDetailPanel';
import SessionFilters from './components/SessionFilters';
import SessionAnalytics from './components/SessionAnalytics';
import NotificationCenter from './components/NotificationCenter';

const SessionManagement = () => {
  const navigate = useNavigate();
  const [selectedSessionId, setSelectedSessionId] = useState(null);
  const [showAnalytics, setShowAnalytics] = useState(false);
  const [showNotifications, setShowNotifications] = useState(false);
  const [filters, setFilters] = useState({
    status: 'all',
    timeRange: 'today',
    participants: 'all'
  });
  const [searchTerm, setSearchTerm] = useState('');
  const [isLoading, setIsLoading] = useState(false);

  // Mock session data
  const mockSessions = [
    {
      id: 'session-1',
      tableNumber: 12,
      tableName: 'Mesa 12 - Cena Familiar',
      participants: 4,
      activeParticipants: 4,
      totalAmount: 89.50,
      paidAmount: 24.50,
      status: 'active',
      completionPercentage: 75,
      startTime: '2025-11-14T19:30:00',
      lastActivity: '2025-11-14T20:28:00'
    },
    {
      id: 'session-2',
      tableNumber: 8,
      tableName: 'Mesa 8 - Reunión Trabajo',
      participants: 6,
      activeParticipants: 5,
      totalAmount: 156.75,
      paidAmount: 0,
      status: 'payment-pending',
      completionPercentage: 90,
      startTime: '2025-11-14T19:00:00',
      lastActivity: '2025-11-14T20:25:00'
    },
    {
      id: 'session-3',
      tableNumber: 5,
      tableName: 'Mesa 5 - Cumpleaños',
      participants: 8,
      activeParticipants: 7,
      totalAmount: 234.20,
      paidAmount: 180.30,
      status: 'payment-pending',
      completionPercentage: 95,
      startTime: '2025-11-14T18:45:00',
      lastActivity: '2025-11-14T20:20:00'
    },
    {
      id: 'session-4',
      tableNumber: 3,
      tableName: 'Mesa 3 - Almuerzo',
      participants: 3,
      activeParticipants: 3,
      totalAmount: 67.80,
      paidAmount: 67.80,
      status: 'completed',
      completionPercentage: 100,
      startTime: '2025-11-14T18:30:00',
      lastActivity: '2025-11-14T20:10:00'
    },
    {
      id: 'session-5',
      tableNumber: 15,
      tableName: 'Mesa 15 - Cena Romántica',
      participants: 2,
      activeParticipants: 1,
      totalAmount: 78.40,
      paidAmount: 0,
      status: 'disputed',
      completionPercentage: 60,
      startTime: '2025-11-14T19:15:00',
      lastActivity: '2025-11-14T20:05:00'
    },
    {
      id: 'session-6',
      tableNumber: 7,
      tableName: 'Mesa 7 - Amigos',
      participants: 5,
      activeParticipants: 0,
      totalAmount: 125.60,
      paidAmount: 0,
      status: 'expired',
      completionPercentage: 30,
      startTime: '2025-11-14T17:45:00',
      lastActivity: '2025-11-14T19:30:00'
    }
  ];

  const [sessions, setSessions] = useState(mockSessions);

  // Filter sessions based on current filters and search
  const filteredSessions = sessions?.filter(session => {
    // Status filter
    if (filters?.status !== 'all' && session?.status !== filters?.status) {
      return false;
    }

    // Participants filter
    if (filters?.participants !== 'all') {
      const participantCount = session?.participants;
      switch (filters?.participants) {
        case '1-2':
          if (participantCount > 2) return false;
          break;
        case '3-5':
          if (participantCount < 3 || participantCount > 5) return false;
          break;
        case '6+':
          if (participantCount < 6) return false;
          break;
      }
    }

    // Search filter
    if (searchTerm) {
      const searchLower = searchTerm?.toLowerCase();
      return (session?.tableName?.toLowerCase()?.includes(searchLower) ||
      session?.id?.toLowerCase()?.includes(searchLower) || session?.tableNumber?.toString()?.includes(searchLower));
    }

    return true;
  });

  const selectedSession = sessions?.find(s => s?.id === selectedSessionId);
  const activeSessions = sessions?.filter(s => s?.status === 'active')?.length;

  // Event handlers
  const handleViewDetails = (sessionId) => {
    setSelectedSessionId(sessionId);
  };

  const handleExtendTime = (sessionId) => {
    console.log('Extending time for session:', sessionId);
    // Implementation would extend session timeout
  };

  const handleCloseSession = (sessionId) => {
    setSessions(prev => 
      prev?.map(session => 
        session?.id === sessionId 
          ? { ...session, status: 'completed', completionPercentage: 100 }
          : session
      )
    );
  };

  const handleResetSession = (sessionId) => {
    setSessions(prev => 
      prev?.map(session => 
        session?.id === sessionId 
          ? { ...session, status: 'active', completionPercentage: 0, paidAmount: 0 }
          : session
      )
    );
  };

  const handleRemoveParticipant = (participantId) => {
    console.log('Removing participant:', participantId);
    // Implementation would remove participant from session
  };

  const handleAdjustBill = (sessionId) => {
    console.log('Adjusting bill for session:', sessionId);
    // Implementation would open bill adjustment modal
  };

  const handleSendReminder = (participantId) => {
    console.log('Sending reminder to participant:', participantId);
    // Implementation would send notification to participant
  };

  const handleFilterChange = (newFilters) => {
    setFilters(newFilters);
  };

  const handleSearch = (term) => {
    setSearchTerm(term);
  };

  const handleRefresh = () => {
    setIsLoading(true);
    // Simulate API call
    setTimeout(() => {
      setIsLoading(false);
    }, 1000);
  };

  const handleMarkAsRead = (notificationId) => {
    console.log('Marking notification as read:', notificationId);
  };

  const handleMarkAllAsRead = () => {
    console.log('Marking all notifications as read');
  };

  const handleResolveIssue = (notificationId, sessionId) => {
    setSelectedSessionId(sessionId);
    setShowNotifications(false);
  };

  const handleDismissNotification = (notificationId) => {
    console.log('Dismissing notification:', notificationId);
  };

  const handleNavigation = (path) => {
    navigate(path);
  };

  return (
    <div className="min-h-screen bg-background">
      <Header />
      <div className="max-w-7xl mx-auto px-4 py-6 space-y-6">
        {/* Page Header */}
        <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4">
          <div>
            <h1 className="text-2xl font-bold text-foreground">
              Gestión de Sesiones
            </h1>
            <p className="text-text-secondary">
              Supervisa y gestiona las sesiones activas de división de cuentas
            </p>
          </div>
          
          <div className="flex items-center space-x-2">
            <Button
              variant={showAnalytics ? "default" : "outline"}
              size="sm"
              onClick={() => setShowAnalytics(!showAnalytics)}
              iconName="BarChart3"
              iconSize={16}
            >
              Análisis
            </Button>
            
            <Button
              variant={showNotifications ? "default" : "outline"}
              size="sm"
              onClick={() => setShowNotifications(!showNotifications)}
              iconName="Bell"
              iconSize={16}
            >
              Notificaciones
            </Button>
            
            <Button
              variant="default"
              size="sm"
              onClick={() => handleNavigation('/qr-code-scanner')}
              iconName="Plus"
              iconSize={16}
            >
              Nueva Sesión
            </Button>
          </div>
        </div>

        {/* Quick Stats */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
          <div className="bg-surface border border-border rounded-lg p-4">
            <div className="flex items-center space-x-2">
              <Icon name="Activity" size={16} className="text-success" />
              <span className="text-sm text-text-secondary">Sesiones Activas</span>
            </div>
            <div className="text-2xl font-bold text-foreground font-mono mt-1">
              {activeSessions}
            </div>
          </div>
          
          <div className="bg-surface border border-border rounded-lg p-4">
            <div className="flex items-center space-x-2">
              <Icon name="Users" size={16} className="text-primary" />
              <span className="text-sm text-text-secondary">Total Participantes</span>
            </div>
            <div className="text-2xl font-bold text-foreground font-mono mt-1">
              {sessions?.reduce((sum, s) => sum + s?.participants, 0)}
            </div>
          </div>
          
          <div className="bg-surface border border-border rounded-lg p-4">
            <div className="flex items-center space-x-2">
              <Icon name="DollarSign" size={16} className="text-warning" />
              <span className="text-sm text-text-secondary">Ingresos Pendientes</span>
            </div>
            <div className="text-2xl font-bold text-foreground font-mono mt-1">
              €{(sessions?.reduce((sum, s) => sum + (s?.totalAmount - s?.paidAmount), 0))?.toFixed(0)}
            </div>
          </div>
          
          <div className="bg-surface border border-border rounded-lg p-4">
            <div className="flex items-center space-x-2">
              <Icon name="CheckCircle" size={16} className="text-success" />
              <span className="text-sm text-text-secondary">Tasa Finalización</span>
            </div>
            <div className="text-2xl font-bold text-foreground font-mono mt-1">
              {Math.round((sessions?.filter(s => s?.status === 'completed')?.length / sessions?.length) * 100)}%
            </div>
          </div>
        </div>

        {/* Analytics Panel */}
        {showAnalytics && (
          <SessionAnalytics 
            timeRange={filters?.timeRange}
          />
        )}

        {/* Notifications Panel */}
        {showNotifications && (
          <NotificationCenter
            onMarkAsRead={handleMarkAsRead}
            onMarkAllAsRead={handleMarkAllAsRead}
            onResolveIssue={handleResolveIssue}
            onDismiss={handleDismissNotification}
          />
        )}

        {/* Filters */}
        <SessionFilters
          onFilterChange={handleFilterChange}
          onSearch={handleSearch}
          onRefresh={handleRefresh}
          totalSessions={sessions?.length}
          activeSessions={activeSessions}
        />

        {/* Main Content */}
        <div className="grid grid-cols-1 xl:grid-cols-3 gap-6">
          {/* Sessions Table */}
          <div className="xl:col-span-2">
            <SessionTable
              sessions={filteredSessions}
              onViewDetails={handleViewDetails}
              onExtendTime={handleExtendTime}
              onCloseSession={handleCloseSession}
              onRemoveParticipant={handleRemoveParticipant}
            />
          </div>

          {/* Session Detail Panel */}
          <div className="xl:col-span-1">
            <SessionDetailPanel
              session={selectedSession}
              onClose={() => setSelectedSessionId(null)}
              onResetSession={handleResetSession}
              onRemoveParticipant={handleRemoveParticipant}
              onAdjustBill={handleAdjustBill}
              onSendReminder={handleSendReminder}
            />
          </div>
        </div>

        {/* Mobile Navigation */}
        <div className="md:hidden fixed bottom-4 left-4 right-4 bg-surface border border-border rounded-lg p-3 shadow-elevation-3">
          <div className="flex items-center justify-around">
            <Button
              variant="ghost"
              size="sm"
              onClick={() => handleNavigation('/qr-code-scanner')}
              iconName="QrCode"
              iconSize={16}
            >
              Escanear
            </Button>
            <Button
              variant="ghost"
              size="sm"
              onClick={() => handleNavigation('/bill-overview')}
              iconName="Receipt"
              iconSize={16}
            >
              Cuentas
            </Button>
            <Button
              variant="ghost"
              size="sm"
              onClick={() => handleNavigation('/item-selection')}
              iconName="ShoppingCart"
              iconSize={16}
            >
              Artículos
            </Button>
            <Button
              variant="ghost"
              size="sm"
              onClick={() => handleNavigation('/payment-summary')}
              iconName="CreditCard"
              iconSize={16}
            >
              Pagos
            </Button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default SessionManagement;